const ROOT_SELECTOR = "[data-try-on-availability-root]";
const DEFAULT_ENDPOINT = "/apps/partner-selection/try-on-availability";

function getRoot() {
  return document.querySelector(ROOT_SELECTOR);
}

function setText(root, selector, value) {
  const el = root.querySelector(selector);
  if (!el) return;
  el.textContent = value || "";
}

function toggleHidden(el, shouldHide) {
  if (!el) return;
  el.classList.toggle("hidden", shouldHide);
}

function setInfoPopover(root, options) {
  const infoButton = root.querySelector("[data-try-on-info-button]");
  const popover = root.querySelector("[data-try-on-popover]");
  const infoText = root.querySelector("[data-try-on-info-text]");
  const infoLink = root.querySelector("[data-try-on-info-link]");

  const visible = Boolean(options?.visible);
  toggleHidden(infoButton, !visible);

  if (!visible) {
    toggleHidden(popover, true);
    if (infoButton) infoButton.setAttribute("aria-expanded", "false");
    return;
  }

  if (infoText) infoText.textContent = options.text || "";

  if (infoLink) {
    const hasLink = Boolean(options.link);
    infoLink.textContent = options.linkLabel || "";
    infoLink.href = hasLink ? options.link : "#";
    toggleHidden(infoLink, !hasLink);
  }
}

function ensureInfoToggleBinding(root) {
  const infoButton = root.querySelector("[data-try-on-info-button]");
  const popover = root.querySelector("[data-try-on-popover]");
  if (!infoButton || !popover) return;

  if (infoButton.dataset.bound === "1") return;
  infoButton.dataset.bound = "1";

  infoButton.addEventListener("click", (event) => {
    event.preventDefault();
    const willOpen = popover.classList.contains("hidden");
    toggleHidden(popover, !willOpen);
    infoButton.setAttribute("aria-expanded", willOpen ? "true" : "false");
  });

  document.addEventListener("click", (event) => {
    if (popover.classList.contains("hidden")) return;
    if (root.contains(event.target)) return;
    toggleHidden(popover, true);
    infoButton.setAttribute("aria-expanded", "false");
  });
}

function getCopy(root) {
  return {
    textAtelierPrefix: root.dataset.textAtelierPrefix || "Essayage disponible au",
    showroomName: root.dataset.showroomName || "Showroom Trolet",
    textPartnerPrefix: root.dataset.textPartnerPrefix || "Essayage disponible au",
    fallbackPartnerName: root.dataset.fallbackPartnerName || "partenaire",
    unavailableText: root.dataset.unavailableText || "Essayage indisponible",
    unknownText: root.dataset.unknownText || "Disponibilite d'essayage a confirmer",
    infoTemplate:
      root.dataset.infoTemplate ||
      "{{partner_name}} est situe a l'adresse suivante : {{partner_address}}",
    infoLinkLabel: root.dataset.infoLinkLabel || "Ouvrir dans Google Maps",
    mapsBaseUrl:
      root.dataset.mapsBaseUrl ||
      "https://www.google.com/maps/search/?api=1&query=",
  };
}

function buildInfoText(template, partnerName, partnerAddress) {
  return String(template || "")
    .replaceAll("[partner_name]", partnerName || "")
    .replaceAll("[partner_address]", partnerAddress || "")
    // Backward compatibility with older template token format.
    .replaceAll("{{partner_name}}", partnerName || "")
    .replaceAll("{{partner_address}}", partnerAddress || "")
    .trim();
}

function buildMapsLink(baseUrl, partnerAddress, explicitUrl) {
  if (explicitUrl) return explicitUrl;
  if (!partnerAddress) return "";
  return `${baseUrl}${encodeURIComponent(partnerAddress)}`;
}

function setLine(root, prefix, place, unavailable = false) {
  setText(root, "[data-try-on-prefix]", `${prefix} `);
  setText(root, "[data-try-on-place]", place || "");
  root.classList.toggle("partner-try-on-availability__state--unavailable", unavailable);
}

function getCurrentVariantId(root) {
  const fromUrl = new URL(window.location.href).searchParams.get("variant");
  const variantId = Number(fromUrl || root.dataset.variantId || 0);
  return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
}

function renderAvailability(root, response) {
  const availability = response?.try_on || {};
  const status = String(availability.status || "").trim();
  const copy = getCopy(root);

  // Close popover on each refresh.
  const popover = root.querySelector("[data-try-on-popover]");
  if (popover) popover.classList.add("hidden");

  if (response?.ok !== true) {
    setLine(root, "", copy.unknownText, true);
    setInfoPopover(root, { visible: false });
    return;
  }

  if (
    status === "selected" ||
    status === "picked_up_by_trolet" ||
    status === "cancelled_by_partner" ||
    status === "atelier_ready"
  ) {
    setLine(root, copy.textAtelierPrefix, copy.showroomName, false);
    setInfoPopover(root, { visible: false });
    return;
  }

  if (
    status === "sent_to_partner" ||
    status === "return_requested" ||
    status === "returned_by_partner_customer"
  ) {
    const partnerName = String(
      availability.partner_name || copy.fallbackPartnerName || ""
    ).trim();
    const partnerAddress = String(
      availability.partner_address ||
        availability.address ||
        availability.partner_location_address ||
        ""
    ).trim();
    const mapsUrl = buildMapsLink(
      copy.mapsBaseUrl,
      partnerAddress,
      availability.partner_maps_url
    );

    setLine(root, copy.textPartnerPrefix, partnerName, false);

    const infoText = buildInfoText(copy.infoTemplate, partnerName, partnerAddress);
    setInfoPopover(root, {
      visible: Boolean(partnerAddress),
      text: infoText,
      link: mapsUrl,
      linkLabel: copy.infoLinkLabel,
    });
    return;
  }

  if (status === "return_to_trolet_requested") {
    setLine(root, "", "Indisponible (retour vers Trolet en attente)", true);
    setInfoPopover(root, { visible: false });
    return;
  }

  if (status === "sold_by_partner" || status === "returned_damaged") {
    setLine(root, "", copy.unavailableText, true);
    setInfoPopover(root, { visible: false });
    return;
  }

  setLine(root, "", copy.unknownText, true);
  setInfoPopover(root, { visible: false });
}

async function fetchAvailability(variantId) {
  const root = getRoot();
  if (!root || !variantId) return;
  const copy = getCopy(root);

  setLine(root, "", copy.unknownText, false);
  setInfoPopover(root, { visible: false });

  const endpoint = root.dataset.endpoint || DEFAULT_ENDPOINT;
  const url = `${endpoint}?variant_id=${encodeURIComponent(variantId)}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      renderAvailability(root, { ok: false });
      return;
    }

    renderAvailability(root, payload);
  } catch {
    renderAvailability(root, { ok: false });
  }
}

function onVariantUpdate(event) {
  const root = getRoot();
  if (!root) return;

  const variantId = Number(event?.detail?.resource?.id || 0);
  if (!variantId || !Number.isFinite(variantId)) return;

  root.dataset.variantId = String(variantId);
  fetchAvailability(variantId);
}

function initTryOnAvailability() {
  const root = getRoot();
  if (!root) return;

  ensureInfoToggleBinding(root);

  const variantId = getCurrentVariantId(root);
  if (variantId) {
    root.dataset.variantId = String(variantId);
    fetchAvailability(variantId);
  } else {
    const copy = getCopy(root);
    setLine(root, "", copy.unknownText, true);
    setInfoPopover(root, { visible: false });
  }

  document.addEventListener("variant:update", onVariantUpdate);
}

initTryOnAvailability();
